import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import {
  useIntent,
  STAGE_CONFIG,
  TRACK_COPY,
  STAGE_HEADLINES,
  SIGNUP_HEADLINES,
  type IntentStage,
  type IntentTrack,
} from "@/context/IntentContext";
import { signup, saveOnboardingCredentials } from "@/api/auth.api";
import { useAuth } from "@/context/AuthContext";

type Step = "stage" | "track" | "signup" | "success";

export function IntentSelectorModal() {
  const { isOpen, close, state, setState } = useIntent();
  const [step, setStep] = useState<Step>("stage");
  const [direction, setDirection] = useState<"forward" | "back">("forward");

  // Reset to step 1 when modal opens — render-adjust instead of an effect
  const [prevOpen, setPrevOpen] = useState(isOpen);
  if (prevOpen !== isOpen) {
    setPrevOpen(isOpen);
    if (isOpen) {
      setStep("stage");
      setDirection("forward");
    }
  }

  // Escape key closes modal
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [isOpen, close]);

  if (!isOpen) return null;

  const accent = state.stage ? STAGE_CONFIG[state.stage].color : "#b87800";

  const goToStep = (next: Step) => {
    const order: Step[] = ["stage", "track", "signup", "success"];
    const cur = order.indexOf(step);
    const tgt = order.indexOf(next);
    setDirection(tgt > cur ? "forward" : "back");
    setStep(next);
  };

  const handleStageSelect = (stage: IntentStage) => {
    setState((s) => ({ ...s, stage }));
    setTimeout(() => goToStep("track"), 300);
  };

  const handleTrackSelect = (track: IntentTrack) => {
    setState((s) => ({ ...s, track }));
    setTimeout(() => goToStep("signup"), 300);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 animate-[fade-in_300ms_ease-out]"
      style={{ background: "rgba(8,8,18,0.85)", backdropFilter: "blur(8px)" }}
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-labelledby="intent-modal-title"
    >
      <div
        className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto"
        style={{
          background: "linear-gradient(180deg, #313949 0%, #1e1f2c 100%)",
          boxShadow: `0 0 40px 4px rgba(${state.stage ? STAGE_CONFIG[state.stage].colorRgb : "130,119,224"}, 0.18), 0 24px 60px rgba(0,0,0,0.6)`,
          border: `1px solid rgba(${state.stage ? STAGE_CONFIG[state.stage].colorRgb : "130,119,224"}, 0.2)`,
          animation: "scale-up 350ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Progress bar */}
        {step !== "success" && (
          <div className="px-5 sm:px-8 pt-6 sm:pt-8 pb-2">
            <ProgressBar step={step} accent={accent} />
            <div className="flex items-center gap-3 mt-3">
              {step !== "stage" && (
                <button
                  onClick={() => goToStep(step === "track" ? "stage" : "track")}
                  className="text-[10px] text-text-muted hover:text-text-primary transition-colors flex items-center gap-1 uppercase tracking-[0.15em] font-medium"
                >
                  <span>{"\u2190"}</span> Back
                </button>
              )}
              <span className="text-[10px] text-text-muted uppercase tracking-[0.2em] font-medium">
                Step {step === "stage" ? 1 : step === "track" ? 2 : 3} of 3
              </span>
            </div>
          </div>
        )}

        {/* Content */}
        <div
          key={step}
          className="px-5 sm:px-8 pb-6 sm:pb-8"
          style={{
            animation: `${
              direction === "forward" ? "slide-in-right" : "slide-in-left"
            } 350ms cubic-bezier(0.22, 1, 0.36, 1)`,
          }}
        >
          {step === "stage" && <StageStep onSelect={handleStageSelect} selected={state.stage} />}
          {step === "track" && state.stage && (
            <TrackStep stage={state.stage} onSelect={handleTrackSelect} selected={state.track} />
          )}
          {step === "signup" && state.stage && state.track && (
            <SignupStep onComplete={() => goToStep("success")} />
          )}
          {step === "success" && state.stage && <SuccessStep />}
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-up {
          from { opacity: 0; transform: scale(0.94); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(24px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slide-in-left {
          from { opacity: 0; transform: translateX(-24px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes scale-pop {
          0% { transform: scale(0); }
          60% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

function ProgressBar({ step, accent }: { step: Step; accent: string }) {
  const segments = [
    { key: "stage", active: ["stage", "track", "signup"].includes(step) },
    { key: "track", active: ["track", "signup"].includes(step) },
    { key: "signup", active: step === "signup" },
  ];

  return (
    <div className="flex gap-1.5">
      {segments.map((s) => (
        <div
          key={s.key}
          className="flex-1 h-1 rounded-full transition-all duration-300"
          style={{
            background: s.active ? accent : "rgba(255,255,255,0.08)",
            boxShadow: s.active ? `0 0 12px ${accent}66` : "none",
          }}
        />
      ))}
    </div>
  );
}

// ── Step 1: Stage Selector ──

function StageStep({ onSelect, selected }: { onSelect: (s: IntentStage) => void; selected: IntentStage | null }) {
  return (
    <>
      <h2
        id="intent-modal-title"
        className="font-extrabold text-text-primary tracking-tight leading-[0.95] mb-2"
        style={{ fontSize: "clamp(22px, 4vw, 30px)" }}
      >
        WHERE ARE YOU IN YOUR FRINGE JOURNEY?
      </h2>
      <p className="text-text-secondary text-xs sm:text-sm mb-6 leading-relaxed">
        This shapes everything — your dashboard, your insights, your first action.
      </p>

      <div className="flex flex-col gap-3">
        {(Object.keys(STAGE_CONFIG) as IntentStage[]).map((key) => {
          const cfg = STAGE_CONFIG[key];
          const isSelected = selected === key;
          return (
            <button
              key={key}
              onClick={() => onSelect(key)}
              className="group relative text-left p-4 sm:p-5 transition-all duration-200 hover:translate-x-1"
              style={{
                background: "rgba(255,255,255,0.025)",
                border: `1px solid ${isSelected ? cfg.color : "rgba(255,255,255,0.08)"}`,
                boxShadow: isSelected
                  ? `inset 4px 0 0 0 ${cfg.color}, 0 0 24px rgba(${cfg.colorRgb},0.15)`
                  : "none",
              }}
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div
                  className="w-10 h-10 sm:w-11 sm:h-11 flex-shrink-0 flex items-center justify-center transition-all"
                  style={{
                    background: `rgba(${cfg.colorRgb}, ${isSelected ? "0.18" : "0.1"})`,
                    border: `1px solid rgba(${cfg.colorRgb}, 0.25)`,
                  }}
                >
                  <svg
                    className="w-5 h-5 sm:w-5.5 sm:h-5.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke={cfg.color}
                    strokeWidth={1.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d={cfg.icon} />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="font-bold text-text-primary text-[14px] sm:text-[15px] leading-tight uppercase tracking-tight"
                    style={{ textShadow: "0 0 20px #222435" }}
                  >
                    {cfg.label}
                  </p>
                  <p className="text-[11px] sm:text-xs text-text-secondary mt-1">{cfg.sub}</p>
                  <p
                    className="text-[10px] sm:text-[11px] mt-2 transition-opacity duration-200 opacity-0 group-hover:opacity-100 leading-relaxed"
                    style={{ color: cfg.color }}
                  >
                    {cfg.detail}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

// ── Step 2: Track Selector ──

function TrackStep({
  stage,
  onSelect,
  selected,
}: {
  stage: IntentStage;
  onSelect: (t: IntentTrack) => void;
  selected: IntentTrack | null;
}) {
  const headline = STAGE_HEADLINES[stage];
  const accent = STAGE_CONFIG[stage].color;
  const accentRgb = STAGE_CONFIG[stage].colorRgb;

  return (
    <>
      <h2
        className="font-extrabold text-text-primary tracking-tight leading-[0.95] mb-2"
        style={{ fontSize: "clamp(22px, 4vw, 30px)" }}
      >
        {headline.title}
      </h2>
      <p className="text-text-secondary text-xs sm:text-sm mb-6 leading-relaxed">{headline.sub}</p>

      <div className="flex flex-col gap-3">
        {(Object.keys(TRACK_COPY) as IntentTrack[]).map((key) => {
          const cfg = TRACK_COPY[key];
          const isSelected = selected === key;
          return (
            <button
              key={key}
              onClick={() => onSelect(key)}
              className="group relative text-left p-4 sm:p-5 transition-all duration-200 hover:translate-x-1"
              style={{
                background: "rgba(255,255,255,0.025)",
                border: `1px solid ${isSelected ? accent : "rgba(255,255,255,0.08)"}`,
                boxShadow: isSelected
                  ? `inset 4px 0 0 0 ${accent}, 0 0 24px rgba(${accentRgb},0.15)`
                  : "none",
              }}
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-11 sm:h-11 flex-shrink-0 flex items-center justify-center text-2xl">
                  {cfg.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="font-bold text-text-primary text-[14px] sm:text-[15px] leading-tight uppercase tracking-tight"
                    style={{ textShadow: "0 0 20px #222435" }}
                  >
                    {cfg.label}
                  </p>
                  <p className="text-[11px] sm:text-xs text-text-secondary mt-1.5 leading-relaxed">
                    {cfg.descriptions[stage]}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

// ── Step 3: Signup ──

function SignupStep({ onComplete }: { onComplete: () => void }) {
  const { state, setState } = useIntent();
  const { refreshSession } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!state.stage || !state.track) return null;

  const stageCfg = STAGE_CONFIG[state.stage];
  const trackCfg = TRACK_COPY[state.track];
  const headlineCfg = SIGNUP_HEADLINES[state.stage];

  const valid = state.firstName.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setError("");
    setSubmitting(true);
    try {
      // Generate a temporary password — user sets real one via verification email
      const tempPassword = `tmp-${Math.random().toString(36).slice(2, 12)}-${Date.now().toString(36)}`;
      // Persist intent state to localStorage for post-signup pickup
      localStorage.setItem(
        "smt_intent",
        JSON.stringify({ stage: state.stage, track: state.track, promoCode: state.promoCode })
      );
      await signup(state.email.trim(), tempPassword, state.firstName.trim());
      await refreshSession();
      onComplete();
    } catch (err: any) {
      setError(err?.message || "Something went wrong. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Context banner */}
      <div className="flex flex-wrap items-center gap-2 mb-5 pb-4 border-b border-white/5">
        <span
          className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] rounded-sm"
          style={{
            background: `rgba(${stageCfg.colorRgb}, 0.15)`,
            color: stageCfg.color,
            border: `1px solid rgba(${stageCfg.colorRgb}, 0.3)`,
          }}
        >
          {stageCfg.tag}
        </span>
        <span className="text-[10px] text-text-muted uppercase tracking-[0.15em]">
          Track: <span className="text-text-secondary font-medium">{trackCfg.label}</span>
        </span>
      </div>

      <h2
        className="font-extrabold text-text-primary tracking-tight leading-[0.95] mb-2"
        style={{ fontSize: "clamp(22px, 4vw, 30px)" }}
      >
        {headlineCfg.title}
      </h2>
      <p className="text-text-secondary text-xs sm:text-sm mb-6 leading-relaxed">
        First name and email.
      </p>

      {error && (
        <div className="mb-4 p-3 text-xs rounded-sm" style={{ background: "rgba(203,80,95,0.1)", color: "#cb505f", border: "1px solid rgba(203,80,95,0.3)" }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <FormInput
          label="First Name"
          value={state.firstName}
          onChange={(v) => setState((s) => ({ ...s, firstName: v }))}
          placeholder="What should we call you?"
          autoFocus
          accent={stageCfg.color}
        />
        <FormInput
          label="Email"
          type="email"
          value={state.email}
          onChange={(v) => setState((s) => ({ ...s, email: v }))}
          placeholder="you@email.com"
          accent={stageCfg.color}
        />
        <button
          type="submit"
          disabled={!valid || submitting}
          className="mt-3 px-6 py-3 sm:py-3.5 font-bold uppercase tracking-[0.12em] text-[12px] sm:text-[13px] text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed enabled:hover:brightness-110"
          style={{
            background: valid
              ? `linear-gradient(135deg, ${stageCfg.color}, ${stageCfg.color}dd)`
              : "rgba(255,255,255,0.06)",
            boxShadow: valid ? `0 0 24px rgba(${stageCfg.colorRgb}, 0.4)` : "none",
          }}
        >
          {submitting ? "Building your account..." : `${headlineCfg.button} \u2192`}
        </button>

        <p className="text-[10px] text-text-muted text-center mt-2 leading-relaxed">
          No credit card required. Free forever for past show analytics.
        </p>
      </form>
    </>
  );
}

function FormInput({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  autoFocus,
  accent,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  type?: string;
  autoFocus?: boolean;
  accent: string;
  hint?: string;
}) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-[0.15em] text-text-muted font-medium">{label}</span>
        {hint && <span className="text-[10px] font-medium" style={{ color: "#28da90" }}>{hint}</span>}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="w-full px-3 py-2.5 text-sm text-text-primary bg-transparent border focus:outline-none transition-all placeholder:text-text-muted/60 placeholder:font-normal"
        style={{
          borderColor: "rgba(255,255,255,0.1)",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = accent;
          e.target.style.boxShadow = `0 0 0 3px rgba(255,255,255,0.02), 0 0 12px ${accent}33`;
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "rgba(255,255,255,0.1)";
          e.target.style.boxShadow = "none";
        }}
      />
    </label>
  );
}

// ── Success Screen ──

interface ApiFestival {
  id: number;
  name: string;
  slug: string;
  ticketing_system: string;
  onboarding_username_label: string | null;
  onboarding_username_placeholder: string | null;
  onboarding_password_label: string | null;
  onboarding_password_placeholder: string | null;
  onboarding_password_masked: number;
  onboarding_org_code: string | null;
  onboarding_require_com_prefix: number;
  onboarding_search_terms: string[];
  onboarding_help_direct_html: string | null;
  onboarding_url_placeholder: string | null;
  onboarding_url_hint: string | null;
  seasons: { year: number; season_start: string | null; season_end: string | null }[];
}

function useFestivalConfig() {
  const [festivals, setFestivals] = useState<ApiFestival[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch("/api/festivals/active")
      .then((r) => r.json())
      .then((j) => { if (j.ok) setFestivals(j.data); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const options: string[] = [];
  const configMap = new Map<string, ApiFestival>();
  for (const f of festivals) {
    for (const s of f.seasons) {
      const label = `${f.name} ${s.year}`;
      options.push(label);
      configMap.set(label, f);
    }
  }
  options.push("Other Festival");

  return { festivals, options, configMap, loaded };
}

const OTHER_FESTIVAL_DEFAULTS = {
  usernameLabel: "Festival Username",
  usernamePlaceholder: "your username",
  passwordLabel: "Festival Password",
  passwordPlaceholder: "your password",
  searchTerms: ["Box Office", "Venue Portal", "Account Login"],
};

function SuccessStep() {
  const { state, close } = useIntent();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { options: festivalOptions, configMap } = useFestivalConfig();
  const [festival, setFestival] = useState("");
  const [username, setUsername] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [loginUrl, setLoginUrl] = useState("");
  const [synced, setSynced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showHelp, setShowHelp] = useState(false);

  if (!state.stage) return null;

  const stageCfg = STAGE_CONFIG[state.stage];
  const accent = stageCfg.color;
  const accentRgb = stageCfg.colorRgb;

  const festivalTimeWord = state.stage === "previous" ? "previous" : "upcoming";

  // Look up festival config from API data
  const festivalCfg = configMap.get(festival) ?? null;
  const festivalName = festival.replace(/\s+\d{4}$/, "");
  const isOtherFestival = festival === "Other Festival";

  const uLabel = festivalCfg?.onboarding_username_label ?? OTHER_FESTIVAL_DEFAULTS.usernameLabel;
  const uPlaceholder = festivalCfg?.onboarding_username_placeholder ?? OTHER_FESTIVAL_DEFAULTS.usernamePlaceholder;
  const pLabel = festivalCfg?.onboarding_password_label ?? OTHER_FESTIVAL_DEFAULTS.passwordLabel;
  const pPlaceholder = festivalCfg?.onboarding_password_placeholder ?? OTHER_FESTIVAL_DEFAULTS.passwordPlaceholder;
  const pMasked = !!festivalCfg?.onboarding_password_masked;
  const requireComPrefix = !!festivalCfg?.onboarding_require_com_prefix;
  const isTicketSearch = festivalCfg?.ticketing_system?.toLowerCase() === "ticketsearch";
  const orgCode = festivalCfg?.onboarding_org_code ?? null;
  const searchTerms = festivalCfg?.onboarding_search_terms?.length ? festivalCfg.onboarding_search_terms : OTHER_FESTIVAL_DEFAULTS.searchTerms;
  const helpDirectHtml = festivalCfg?.onboarding_help_direct_html ?? null;

  const trimmedUsername = username.trim();
  const usernameStartsWithCom = trimmedUsername.toUpperCase().startsWith("COM");
  const usernameFormatValid = !requireComPrefix || usernameStartsWithCom;
  const showUsernameError = requireComPrefix && trimmedUsername.length > 0 && !usernameStartsWithCom;

  const isOtherFestivalUrl = isOtherFestival;
  const trimmedUrl = loginUrl.trim();
  const urlValid = !isOtherFestivalUrl || trimmedUrl.length > 0;

  const credsValid =
    trimmedUsername.length > 0 &&
    emailPassword.trim().length > 0 &&
    usernameFormatValid &&
    urlValid;

  const handleSync = async () => {
    if (!credsValid || submitting) return;
    setError("");
    setSubmitting(true);
    try {
      // For "Other Festival", embed the login URL into the label so admin can configure later
      const label = isOtherFestival
        ? `${festival} \u2014 ${trimmedUrl}`
        : festival;
      if (isTicketSearch) {
        await saveOnboardingCredentials([
          {
            red61_username: orgCode || "UNKNOWN",
            red61_email: trimmedUsername,
            label,
            password: emailPassword.trim(),
          },
        ]);
      } else {
        await saveOnboardingCredentials([
          {
            red61_username: trimmedUsername,
            red61_email: emailPassword.trim(),
            label,
          },
        ]);
      }
      setSynced(true);
      // Refresh shows list after sync completes so new shows appear in selector
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["shows"] }), 3000);
    } catch (err: any) {
      setError(err?.message || "Couldn't save credentials. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCta = () => {
    // Clear stored show so ShowContext auto-selects the newest show
    localStorage.removeItem("smt_selected_show");
    queryClient.invalidateQueries({ queryKey: ["shows"] });
    close();
    navigate("/dashboard");
  };

  return (
    <div className="text-center py-2 px-1">
      <div
        className="text-5xl mb-4 inline-block"
        style={{ animation: "scale-pop 500ms cubic-bezier(0.22, 1, 0.36, 1)" }}
      >
        {"\uD83C\uDFAD"}
      </div>
      <h2
        className="font-extrabold text-text-primary tracking-tight leading-[0.95] mb-2 uppercase"
        style={{ fontSize: "clamp(22px, 4vw, 30px)" }}
      >
        YOU'RE IN, {state.firstName.toUpperCase()}.
      </h2>
      <p className="text-text-primary text-sm mb-6 leading-relaxed max-w-md mx-auto font-medium">
        Your live dashboard is ready to perform, but it needs your show data!
      </p>

      <div className="flex flex-col gap-3 text-left max-w-md mx-auto">
        {/* STEP 1: Festival selection */}
        <StepBlock number={1} accent={accent} accentRgb={accentRgb} done={!!festival}>
          <p className="text-[12px] font-semibold text-text-primary mb-2 leading-tight">
            Select your <span style={{ color: accent }}>{festivalTimeWord}</span> Festival
          </p>
          <select
            value={festival}
            onChange={(e) => {
              setFestival(e.target.value);
              setSynced(false);
              setError("");
            }}
            disabled={synced}
            className="w-full px-3 py-2.5 text-sm text-text-primary bg-transparent border focus:outline-none transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              borderColor: festival ? accent : "rgba(255,255,255,0.1)",
              boxShadow: festival ? `0 0 12px rgba(${accentRgb}, 0.15)` : "none",
            }}
          >
            <option value="" style={{ background: "#313949" }}>
              Choose your festival...
            </option>
            {festivalOptions.map((f) => (
              <option key={f} value={f} style={{ background: "#313949" }}>
                {f}
              </option>
            ))}
          </select>
        </StepBlock>

        {/* STEP 2: Credentials (only after festival selected) */}
        {!!festival && (
          <StepBlock number={2} accent={accent} accentRgb={accentRgb} done={synced}>
            <p className="text-[12px] font-semibold text-text-primary mb-3 leading-tight">
              Connect your festival login credentials
            </p>

            {synced ? (
              <p className="text-[12px] text-text-secondary leading-relaxed">
                <span className="font-semibold" style={{ color: "#28da90" }}>
                  Saved.
                </span>{" "}
                Sync queued for <span className="font-semibold text-text-primary">{festival}</span>.
              </p>
            ) : (
              <div className="flex flex-col gap-2.5">
                <div>
                  <FormInput
                    label={uLabel}
                    value={username}
                    onChange={setUsername}
                    placeholder={uPlaceholder}
                    accent={accent}
                  />
                  {showUsernameError && (
                    <p className="text-[10px] mt-1.5 leading-snug" style={{ color: "#cb505f" }}>
                      This field must start with <span className="font-mono font-bold">COM</span> &mdash; double-check the format.
                    </p>
                  )}
                </div>
                <FormInput
                  label={pLabel}
                  value={emailPassword}
                  onChange={setEmailPassword}
                  placeholder={pPlaceholder}
                  type={pMasked ? "password" : "text"}
                  accent={accent}
                />
                {isOtherFestival && (
                  <FormInput
                    label="URL Link to Login"
                    value={loginUrl}
                    onChange={setLoginUrl}
                    placeholder="https://your-festival-portal.com/login"
                    accent={accent}
                  />
                )}
                <button
                  type="button"
                  onClick={() => setShowHelp(true)}
                  className="text-left text-[12px] font-bold mt-1 inline-flex items-center gap-1.5 transition-all self-start hover:brightness-110 hover:translate-x-0.5"
                  style={{ color: accent }}
                >
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                  </svg>
                  Can't find your details? Click here for hunting tips.
                </button>

                {error && (
                  <div
                    className="p-2.5 text-[11px]"
                    style={{
                      background: "rgba(203,80,95,0.1)",
                      color: "#cb505f",
                      border: "1px solid rgba(203,80,95,0.3)",
                    }}
                  >
                    {error}
                  </div>
                )}

                <button
                  onClick={handleSync}
                  disabled={!credsValid || submitting}
                  className="w-full mt-1 px-5 py-2.5 font-bold uppercase tracking-[0.12em] text-[11px] text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed enabled:hover:brightness-110"
                  style={{
                    background: credsValid
                      ? `linear-gradient(135deg, ${accent}, ${accent}dd)`
                      : "rgba(255,255,255,0.06)",
                    boxShadow: credsValid ? `0 0 20px rgba(${accentRgb}, 0.35)` : "none",
                  }}
                >
                  {submitting ? "Syncing..." : "Sync Data \u2192"}
                </button>
              </div>
            )}
          </StepBlock>
        )}

        {/* STEP 3: Confirmation (only after sync clicked) */}
        {synced && (
          <StepBlock number={3} accent={accent} accentRgb={accentRgb}>
            <p className="text-[12px] text-text-secondary leading-relaxed">
              Your live dashboard will activate as soon as data sync initiates.{" "}
              <span className="font-bold" style={{ color: accent }}>
                While you're waiting &mdash; jump over to your email to confirm your email address.
              </span>
            </p>
          </StepBlock>
        )}
      </div>

      {/* "Show my Dashboard" appears once step 3 is loaded */}
      {synced && (
        <div className="mt-6">
          <button
            onClick={handleCta}
            className="px-7 py-3 font-bold uppercase tracking-[0.12em] text-[12px] sm:text-[13px] text-white transition-all hover:brightness-110"
            style={{
              background: `linear-gradient(135deg, ${accent}, ${accent}dd)`,
              boxShadow: `0 0 24px rgba(${accentRgb}, 0.4)`,
            }}
          >
            Show My Dashboard {"\u2192"}
          </button>
        </div>
      )}

      {/* Hunting tips popup */}
      {showHelp && (
        <CredentialHelpPopup
          festivalName={festivalName}
          searchTerms={searchTerms}
          helpDirectHtml={helpDirectHtml}
          accent={accent}
          accentRgb={accentRgb}
          onClose={() => setShowHelp(false)}
        />
      )}
    </div>
  );
}

// Help popup overlay — appears on top of the success screen modal
function CredentialHelpPopup({
  festivalName,
  searchTerms,
  helpDirectHtml,
  accent,
  accentRgb,
  onClose,
}: {
  festivalName: string;
  searchTerms: string[];
  helpDirectHtml: string | null;
  accent: string;
  accentRgb: string;
  onClose: () => void;
}) {
  const isOther = festivalName === "Other Festival" || !festivalName;
  const friendlyFestival = isOther ? "any festival you've performed at" : festivalName;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6"
      style={{ background: "rgba(8,8,18,0.85)", backdropFilter: "blur(8px)", animation: "fade-in 200ms ease-out" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-popup-title"
    >
      <div
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto"
        style={{
          background: "linear-gradient(180deg, #313949 0%, #1e1f2c 100%)",
          border: `1px solid rgba(${accentRgb}, 0.35)`,
          boxShadow: `0 0 40px rgba(${accentRgb}, 0.2), 0 24px 60px rgba(0,0,0,0.6)`,
          animation: "scale-up 300ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-6 sm:p-8">
          {/* Eyebrow */}
          <p
            className="text-[10px] uppercase tracking-[0.25em] font-bold mb-3"
            style={{ color: accent }}
          >
            Hunting Tips
          </p>

          {/* Headline */}
          <h3
            id="help-popup-title"
            className="font-extrabold text-text-primary leading-[1.05] tracking-tight mb-4 uppercase"
            style={{ fontSize: "clamp(20px, 3vw, 26px)" }}
          >
            This is the important step to get
            <br />
            <span style={{ color: accent }}>YOUR show's insights.</span>
          </h3>

          {/* Supportive intro */}
          <p className="text-text-secondary text-[13px] leading-relaxed mb-6">
            Your credentials are sitting in an old email from your festival.
            Once we have them, your show data flows in automatically &mdash; and you
            see <span className="text-text-primary font-semibold">everything</span> that happened. We promise it's worth the dig.
          </p>

          {/* Search terms */}
          <p
            className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3"
            style={{ color: accent }}
          >
            Search your inbox for
          </p>
          <div className="flex flex-col gap-2 mb-6">
            {searchTerms.map((term, i) => (
              <div
                key={term}
                className="flex items-center gap-3 p-3"
                style={{
                  background: "rgba(255,255,255,0.025)",
                  border: `1px solid rgba(${accentRgb}, 0.18)`,
                }}
              >
                <span
                  className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-[10px] font-bold rounded-full"
                  style={{
                    background: `rgba(${accentRgb}, 0.18)`,
                    color: accent,
                    border: `1px solid rgba(${accentRgb}, 0.3)`,
                  }}
                >
                  {i + 1}
                </span>
                <code
                  className="text-[13px] font-mono font-semibold text-text-primary"
                  style={{ letterSpacing: "0.02em" }}
                >
                  "{term}"
                </code>
              </div>
            ))}
          </div>

          {/* Direct access — from festival DB config */}
          {!!helpDirectHtml && (
            <div className="mb-6">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3" style={{ color: accent }}>
                Direct Access
              </p>
              <div className="p-4" style={{ background: "rgba(255,255,255,0.025)", border: `1px solid rgba(${accentRgb}, 0.18)` }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(helpDirectHtml) }} />
            </div>
          )}

          {/* Fallback note */}
          <div
            className="p-4 mb-6"
            style={{
              background: "rgba(40,218,144,0.06)",
              border: "1px solid rgba(40,218,144,0.25)",
            }}
          >
            <p className="text-[12px] text-text-primary leading-relaxed">
              <span className="font-bold" style={{ color: "#28da90" }}>
                Still stuck?
              </span>{" "}
              You can start with <span className="font-semibold">any of your previous login details</span>{" "}
              from {friendlyFestival}, even if it's not the specific year you're after. We can find the rest from there.
            </p>
          </div>

          {/* OK button */}
          <button
            onClick={onClose}
            className="w-full px-5 py-3 font-bold uppercase tracking-[0.12em] text-[12px] text-white transition-all hover:brightness-110"
            style={{
              background: `linear-gradient(135deg, ${accent}, ${accent}dd)`,
              boxShadow: `0 0 20px rgba(${accentRgb}, 0.35)`,
            }}
          >
            Got it &mdash; let's hunt
          </button>
        </div>
      </div>
    </div>
  );
}

function StepBlock({
  number,
  accent,
  accentRgb,
  done,
  children,
}: {
  number: number;
  accent: string;
  accentRgb: string;
  done?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className="flex items-start gap-3 p-3.5"
      style={{
        background: "rgba(255,255,255,0.025)",
        border: `1px solid rgba(${accentRgb}, 0.18)`,
      }}
    >
      <div
        className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-[12px] font-bold rounded-full transition-colors"
        style={{
          background: done ? "rgba(40,218,144,0.18)" : `rgba(${accentRgb}, 0.18)`,
          color: done ? "#28da90" : accent,
          border: `1px solid ${done ? "rgba(40,218,144,0.35)" : `rgba(${accentRgb}, 0.3)`}`,
        }}
      >
        {done ? "\u2713" : number}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}
